// Cross-platform clipboard fallback for CEF builds.
//
// On macOS the CEF host forwards Ctrl/Cmd+C/V/X/A to the renderer so the
// browser's native clipboard handling "just works" inside <input>,
// <textarea>, and contenteditable elements. On Windows this forwarding
// is unreliable. Users can't copy or paste text into any input. Rather
// than hunt for the right CEF command-line flag per release, we bind
// the shortcuts ourselves in JS and call the Clipboard API directly.
//
// Scope:
//   - Only fires when the focused target is an editable element
//   - Ctrl/Cmd+C/V/X/A only; other shortcuts pass through
//   - preventDefault() suppresses any native handling so we don't
//     double-apply on platforms where the browser WOULD have handled it.
//
// Falls back to document.execCommand('copy'|'cut'|'paste'|'selectAll')
// when navigator.clipboard isn't available (shouldn't happen in CEF,
// but harmless).

function isEditable(target) {
  if (!target) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
}

async function readClipboardText() {
  if (navigator.clipboard?.readText) {
    try { return await navigator.clipboard.readText(); } catch {}
  }
  return null;
}

async function writeClipboardText(text) {
  if (navigator.clipboard?.writeText) {
    try { await navigator.clipboard.writeText(text); return true; } catch {}
  }
  return false;
}

function insertTextInField(target, text) {
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA') {
    const start = target.selectionStart ?? target.value.length;
    const end = target.selectionEnd ?? target.value.length;
    const value = target.value;
    target.value = value.slice(0, start) + text + value.slice(end);
    const next = start + text.length;
    target.setSelectionRange(next, next);
    target.dispatchEvent(new Event('input', { bubbles: true }));
  } else if (target.isContentEditable) {
    // contenteditable: use the legacy command so controlled rich-text
    // nodes still see the change. This is still supported in Chromium.
    document.execCommand('insertText', false, text);
  }
}

function removeSelectionInField(target) {
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA') {
    const start = target.selectionStart;
    const end = target.selectionEnd;
    if (start === end) return;
    target.value = target.value.slice(0, start) + target.value.slice(end);
    target.setSelectionRange(start, start);
    target.dispatchEvent(new Event('input', { bubbles: true }));
  } else if (target.isContentEditable) {
    document.execCommand('delete');
  }
}

function getSelectedText(target) {
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA') {
    const start = target.selectionStart;
    const end = target.selectionEnd;
    if (start === end) return '';
    return target.value.slice(start, end);
  }
  if (target.isContentEditable) {
    return window.getSelection()?.toString() ?? '';
  }
  return '';
}

export function installClipboardFallback() {
  window.addEventListener('keydown', async (event) => {
    if (!(event.ctrlKey || event.metaKey)) return;
    if (event.altKey) return; // Ctrl+Alt combos are usually AltGr input. Don't hijack.
    if (!isEditable(event.target)) return;

    const target = event.target;
    const key = event.key.toLowerCase();

    if (key === 'c') {
      const text = getSelectedText(target);
      if (!text) return;
      event.preventDefault();
      event.stopPropagation();
      await writeClipboardText(text);
    } else if (key === 'x') {
      const text = getSelectedText(target);
      if (!text) return;
      event.preventDefault();
      event.stopPropagation();
      if (await writeClipboardText(text)) {
        removeSelectionInField(target);
      }
    } else if (key === 'v' && !event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      const text = await readClipboardText();
      if (text) insertTextInField(target, text);
    } else if (key === 'a') {
      event.preventDefault();
      event.stopPropagation();
      const tag = target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') {
        target.setSelectionRange(0, target.value.length);
      } else if (target.isContentEditable) {
        document.execCommand('selectAll');
      }
    }
  }, true);
}
